import { useCallback, useMemo } from 'react';
import { useLocation, useMatches, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updateState } from 'store/entities/navigation/actions';
import type { AgendaRecordItem, GetAgendaRecordsRes, NavigationUrls } from 'types';
import type { TablePersistentState } from 'components/common/Table/Table';
import {
  calculateNextRecordNavigation,
  calculatePrevRecordNavigation,
  createNavigationStateUpdate,
  getEditRecordUrl,
  getRecordDetailUrl,
} from 'utils/navigation';
import type { RouteHandle } from 'contexts/BreadcrumbsProvider';
import { trackNavigateBetweenRecords } from 'utils/posthog/metrics';

interface UseRecordNavigationProps {
  recordId: string;
  data: GetAgendaRecordsRes<AgendaRecordItem[]>;
  savedNavigationState: TablePersistentState | undefined;
  selectedTab: number;
  resetFormState: () => void;
}

export function useRecordNavigation({ recordId, data, savedNavigationState, selectedTab, resetFormState }: UseRecordNavigationProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { id: moduleId = '', agendaId = '' } = useParams();
  const matches = useMatches();
  const systemType = (matches.find((match) => (match.handle as RouteHandle)?.systemType)?.handle as RouteHandle)?.systemType ?? '';
  const urls: NavigationUrls = useMemo(() => ({ moduleId, systemType, agendaId }), [moduleId, systemType, agendaId]);

  const handleNextRecord = useCallback(() => {
    const navigationResult = calculateNextRecordNavigation({
      recordId,
      data,
      savedNavigationState,
    });

    if (navigationResult.shouldNavigate) {
      try {
        trackNavigateBetweenRecords({
          process: 'record',
          agendaId,
          fromRecordId: recordId,
          toRecordId: navigationResult.targetRecordId,
          direction: 'next',
          area: 'detail_header',
        });
      } catch {
        /* noop */
      }
      dispatch(updateState(createNavigationStateUpdate(navigationResult)));
      resetFormState();
      navigate(getRecordDetailUrl(urls, navigationResult.targetRecordId));
    }
  }, [recordId, data, savedNavigationState, dispatch, resetFormState, navigate, urls, agendaId]);

  const handlePrevRecord = useCallback(() => {
    const navigationResult = calculatePrevRecordNavigation({
      recordId,
      data,
      savedNavigationState,
    });

    if (navigationResult.shouldNavigate) {
      try {
        trackNavigateBetweenRecords({
          process: 'record',
          agendaId,
          fromRecordId: recordId,
          toRecordId: navigationResult.targetRecordId,
          direction: 'prev',
          area: 'detail_header',
        });
      } catch {
        /* noop */
      }
      dispatch(updateState(createNavigationStateUpdate(navigationResult)));
      resetFormState();
      navigate(getRecordDetailUrl(urls, navigationResult.targetRecordId));
    }
  }, [recordId, data, savedNavigationState, dispatch, resetFormState, navigate, urls, agendaId]);

  const handleGoBack = useCallback(() => {
    const agendaPath = pathname.split('/r/')[0];
    const isStateForCurrentAgenda = savedNavigationState?.other?.agendaId === agendaId;
    const savedViewId = isStateForCurrentAgenda ? (savedNavigationState?.other?.tabId ?? savedNavigationState?.other?.viewId) : undefined;
    const targetPath = savedViewId ? `${agendaPath}/view/${savedViewId}` : agendaPath;

    navigate(targetPath, { replace: true });
  }, [navigate, pathname, savedNavigationState, agendaId]);

  const handleNavigateToEdit = useCallback(() => {
    navigate(getEditRecordUrl(urls, recordId), { state: selectedTab });
  }, [navigate, urls, recordId, selectedTab]);

  const currentIndex = (savedNavigationState?.lastClickedRow?.absoluteIndex ?? 0) + 1;
  const showNavigationArrows = currentIndex > 0 && data.totalCount > 0 && savedNavigationState?.lastClickedRow?.id === recordId;
  const canGoPrevious = showNavigationArrows && currentIndex > 1;
  const canGoNext = showNavigationArrows && currentIndex < (data.totalCount ?? 0);

  return {
    handleNextRecord,
    handlePrevRecord,
    handleGoBack,
    handleNavigateToEdit,
    currentIndex,
    showNavigationArrows,
    canGoPrevious,
    canGoNext,
  };
}

export const useRecordDetailScreen = () => {
  const { pathname } = useLocation();
  const hasBase = pathname.includes('/user/modules');
  const hasRecord = pathname.includes('/r/');
  return hasBase && hasRecord;
};
